/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonIcon, IonModal } from "@ionic/react";
import { signInWithEmail } from "../../../services/auth";
import {
  changeUserEmail,
  changeUserPhone,
  verifyEmail,
  verifyPhoneNumber,
} from "../../../services/user";
import { close } from "ionicons/icons";
import { PhoneFormatter } from "../PhoneFormate";
interface OtpPopupProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  setPhone: (val: string) => void;
  countryCode: string;
  setCountryCode: (val: string) => void;
  email: string;
  setEmail: (val: string) => void;
  userPhone: string;
  userEmail: string;
  userCountryCode?: string;
  handleSaveProfile: (contact: string, type: "phone" | "email") => void;
  showToastMessage: (msg: string) => void;
  mode: "phone" | "email";
  skipDuplicateCheck?: boolean;
}
const isValidPhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\+?[\d\s\-()]{10,19}$/;
  if (phone === "") return false;
  return phoneRegex.test(phone.trim());
};

const isValidEmail = (email: string): boolean => {
  if (email === null) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

const OtpPopup: React.FC<OtpPopupProps> = ({
  isOpen,
  onClose,
  phone,
  setPhone,
  email,
  countryCode,
  setCountryCode,
  setEmail,
  userPhone,
  userEmail,
  userCountryCode,
  handleSaveProfile,
  showToastMessage,
  mode,
  skipDuplicateCheck,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [step, setStep] = useState<"input" | "otp">("input");
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [autoDetectAttempted, setAutoDetectAttempted] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isFormated, setIsFormated] = useState<boolean>(true);

  // Local state for input values to prevent using stale props
  const [localPhone, setLocalPhone] = useState(phone);
  const [localEmail, setLocalEmail] = useState("");

  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  // Initialize local values when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      setLocalPhone(phone);
      setLocalEmail(email);
      setStep("input"); // Reset to input step
      setOtp(Array(6).fill(""));
      setError("");
      setSuccess("");
      setErrorMessage("");
      setSuccessMessage("");
      setResendTimer(0);
      setLoading(false);
      setAutoDetectAttempted(false);
      setAuthToken(null);
    }
  }, [isOpen, mode, phone, email]);
  // Resend timer countdown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);
  useEffect(() => {
    if (isOpen && step === "otp") {
      setOtp(Array(6).fill(""));
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
      attemptAutoDetectOTP();
    }
  }, [isOpen, step]);

  const attemptAutoDetectOTP = async () => {
    if (autoDetectAttempted) return;
    setAutoDetectAttempted(true);

    if ("OTPCredential" in window) {
      try {
        const abortController = new AbortController();
        const otpCredential = (await (navigator.credentials as any).get({
          otp: { transport: ["sms"] },
          signal: abortController.signal,
        })) as any | null;

        if (otpCredential && otpCredential.code) {
          const code = otpCredential.code;
          const codeArray = code.split("");
          setOtp(codeArray);

          if (codeArray.length === 6) {
            setTimeout(() => {
              handleVerify(codeArray.join(""));
            }, 500);
          }
        }
      } catch (error) {
        console.warn("Auto-detect OTP failed:", error);
      }
    }
  };

  const handleInputChange = (index: number, value: string) => {
    // Handle paste of full OTP
    if (value.length > 1) {
      if (value.length === 6 && /^\d{6}$/.test(value)) {
        const codeArray = value.split("");
        setOtp(codeArray);
        if (inputRefs.current[5]) {
          inputRefs.current[5].focus();
        }
        return;
      }
      value = value.slice(0, 1);
    }

    // Only allow digits
    if (value && !/^[0-9]$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all digits are entered
    if (newOtp.every((digit) => digit !== "") && newOtp.length === 6) {
      setTimeout(() => {
        handleVerify(newOtp.join(""));
      }, 300);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    const contact =
      mode === "phone"
        ? countryCode + localPhone.replace(/[^\d]/g, "") // remove any other characters
        : localEmail; // no prefixing
    const valid =
      mode === "phone" ? isValidPhoneNumber(contact) : isValidEmail(contact);

    if (!contact.trim()) {
      setError(`Please enter a ${mode}`);
      return;
    }

    if (!valid) {
      setError(
        `Please enter a valid ${mode === "phone" ? "phone number" : "email"}`
      );
      return;
    }

    let sanitizedContact = contact.trim();
    if (mode === "phone") {
      sanitizedContact = contact.replace(/[^\d+]/g, ""); // keep only + and digits
    }

    if (
      (mode === "phone" && sanitizedContact.trim() === userPhone.trim()) ||
      (mode === "email" && contact.trim() === userEmail.trim())
    ) {
      setError(`New ${mode} must be different from current ${mode}`);
      return;
    }
    setLoading(true);
    setError("");
    let result: any;
    try {
      if (mode === "phone") {
        result = await changeUserPhone(contact, countryCode);
        // Extract auth tokens from response
        if (result && result.status === "ok") {
          if (result.token) {
            setAuthToken(result.token);
          }
        }
      } else {
        result = await changeUserEmail(contact);
        if (result && result.status === "ok") {
          setAuthToken(result.token || null);
        }
      }

      if (result && result.status === "ok") {
        // setSuccess("Verification Code sent");
        setStep("otp");
        setAuthToken(result.token || null);
        setResendTimer(60);
        showToastMessage(result.message || "OTP sent successfully!");
      } else {
        setError(result?.message || "Failed to send OTP");
      }
    } catch (error: any) {
      console.error("Send OTP error:", error);
      setError(error.response?.data.message || "Failed to send OTP");
    }
    setLoading(false);
  };

  // Verify OTP
  const handleVerify = async (providedOtp?: string) => {
    const contact =
      mode === "phone"
        ? countryCode + localPhone.replace(/[^\d]/g, "")
        : localEmail;
    const code = providedOtp || otp.join("");
    const token: string = authToken ? authToken : "";

    if (code.length !== 6) {
      setErrorMessage("Please enter a complete verification code");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      let result;
      if (mode === "phone") {
        result = await verifyPhoneNumber(token, code);
      } else {
        result = await verifyEmail(token, code);
      }

      if (result && (result.success || result.status === "ok")) {
        if (mode === "phone") {
          setPhone(contact); // Update parent state with verified contact
        } else {
          setEmail(contact);
        }
        setSuccessMessage(
          `${
            mode === "phone" ? "Phone number" : "Email"
          } verified successfully!`
        );
        showToastMessage(
          `${mode === "phone" ? "Phone number" : "Email"} updated successfully!`
        );
        setAuthToken("");

        // Call the save profile function with the verified contact
        handleSaveProfile(contact, mode);

        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setErrorMessage(result?.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      setErrorMessage("Verification failed. Please try again.");
    }

    setLoading(false);
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const contact = mode === "phone" ? localPhone : localEmail;
      let result: any;

      if (mode === "phone") {
        result = await changeUserPhone(contact, countryCode);
        if (result && result.status === "ok") {
          if (result.token) {
            setAuthToken(result.token);
          }
        }
      } else {
        result = await signInWithEmail(contact);
      }

      if (result && (result.success || result.status === "ok")) {
        setSuccessMessage("Verification code resent!");
        setResendTimer(60);
        setTimeout(() => setSuccessMessage(""), 3000);
        setAuthToken(result.token || null);

        // Reset OTP inputs
        setOtp(Array(6).fill(""));
        setAutoDetectAttempted(false);
        attemptAutoDetectOTP();
      } else {
        setErrorMessage(
          "Failed to resend verification code. Please try again."
        );
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setErrorMessage("Failed to resend verification code. Please try again.");
    }

    setLoading(false);
  };
  let contact: string;
    const sanitize = (str: string) => str.replace(/[()\s_-]/g, "");

  const isFormValid = (): boolean => {
    // Get the contact value based on mode
    contact = mode === "phone" ? localPhone : localEmail;
    if (
      !skipDuplicateCheck &&
      mode === "phone" &&
      userCountryCode === countryCode &&
      (localPhone === "" || localPhone.replace(/[^\d]/g, "") === 
        (userCountryCode && sanitize(userPhone).startsWith(sanitize(userCountryCode))
          ? sanitize(userPhone).slice(sanitize(userCountryCode).length)
          : sanitize(userPhone)))
    ) {
      return false;
    }
    // Check if contact exists and is not null/undefined
    if (!contact || contact === null || contact === undefined) {
      return false;
    }

    // Trim the contact and check if it's empty
    const trimmedContact = contact.trim();
    if (!trimmedContact) {
      return false;
    }

    // Validate based on mode
    if (mode === "phone") {
      const digitsOnly = trimmedContact.replace(/[^\d]/g, "");
      return digitsOnly.length >= 6; // Phone must have at least 6 digits
    } else {
      if(userEmail === trimmedContact){
        return false;
      }
      return isValidEmail(trimmedContact); // Email must be valid
    }
  };
  useEffect(() => {
    isFormValid();
  }, [localPhone, localEmail, isFormated]);
  // Close modal and reset states
  const handleClose = () => {
    setStep("input");
    setOtp(Array(6).fill(""));
    setError("");
    setSuccess("");
    setErrorMessage("");
    setSuccessMessage("");
    setResendTimer(0);
    setLoading(false);
    setAutoDetectAttempted(false);
    setAuthToken(null);
    // setLocalPhone("");
    setLocalEmail("");
    onClose();
  };

  // Handle input change for contact info
  const handleContactChange = (value: string) => {
    if (mode === "phone") {
      setLocalPhone(value);
    } else {
      setLocalEmail(value);
    }
    setError("");
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleClose}
      style={{
        "--border-radius": "30px",
        "--width": "362px",
        "--height": "auto",
        "--max-height": "80vh",
        "--bottom": "0",
        "--background": "white",
      }}
      className="otp-modal"
    >
      <div className="p-6 flex flex-col">
        {/* Header */}
        <div
          className="flex items-center justify-between px-2 mb-4"
          style={{ minHeight: "40px" }}
        >
          <h3
            className="text-black"
            style={{
              width: "309px",
              height: "28px",
              fontFamily: "'Figtree', sans-serif",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "24px",
              paddingTop: "30px",
              lineHeight: "28px",
              textAlign: "center",
              color: "#000000",
              flex: "none",
              order: 0,
              flexGrow: 0,
              zIndex: 0,
              margin: 0,
              padding: 0,
            }}
          >
            {step === "input"
              ? mode === "phone"
                ? "Confirm Phone Number"
                : "Confirm Email Address"
              : mode === "phone"
              ? "Verify Phone Number"
              : "Verify Email Address"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 absolute right-4"
            disabled={isLoading}
          >
            <IonIcon icon={close} style={{ fontSize: "30px" }} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4"
            style={{
              fontFamily: "Figtree",
              fontSize: "14px",
              alignContent: "center",
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div
            className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4"
            style={{ fontFamily: "Figtree", fontSize: "14px" }}
          >
            {success}
          </div>
        )}

        {step === "input" && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                marginBottom: "10px",
              }}
            >
              <p
                style={{
                  width: "322px",
                  minHeight: "52px",
                  fontFamily: "Figtree",
                  fontStyle: "normal",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "26px",
                  textAlign: "center",
                  letterSpacing: "-0.24px",
                  fontFeatureSettings: "'case' on",
                  margin: 0,
                  paddingBottom: "10px",
                }}
              >
                {mode === "email"
                  ? "Confirm your email address by sending a verification code."
                  : "Confirm your phone number by sending a verification code."}
              </p>
            </div>

            {mode === "email" ? (
              <input
                type={"email"}
                placeholder={"Enter new email"}
                value={localEmail} // ← Use the local state instead of hardcoded ""
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  let value = e.target.value || "";
                  if (mode === "email") {
                    if (value) {
                      value = value.trim();
                    }
                  }
                  handleContactChange(value);
                }}
                disabled={isLoading}
                style={{
                  width: "100%",
                  fontSize: "18px",
                  paddingLeft: "10px",
                  height: "48px",
                  fontFamily: "Figtree",
                  marginTop: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  outline: "none",
                }}
                className="custom-ion-input"
              />
            ) : (
              <>
                <PhoneFormatter
                  setIsFormated={setIsFormated}
                  setCountryCode={setCountryCode}
                  CountryCode={countryCode}
                  phone={localPhone} // Pass local state
                  setPhone={setLocalPhone} // Pass local setter
                  handlePhoneInput={(
                    e: React.ChangeEvent<HTMLInputElement>
                  ) => {
                    const value = e.target.value || "";
                    handleContactChange(value);
                  }}
                />
              </>
            )}
            {/* <p
              style={{
                width: "322px",
                minHeight: "52px",
                fontFamily: "Figtree",
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "26px",
                textAlign: "left",
                letterSpacing: "-0.24px",
                fontFeatureSettings: "'case' on",
                color: "#686363ff",
                margin: 0,
              }}
            >
              Current Phone Number: {userPhone || "Not set"}
            </p> */}
            <div
              className="flex flex-col gap-2 mb-4"
              style={{ paddingTop: "20px", alignItems: "center" }}
            >
              <IonButton
                expand="block"
                onClick={handleSendOtp}
                disabled={isLoading || !isFormValid()}
                className="primary-button w-full mb-4"
                style={{
                  "--border-radius": "30px",
                  fontSize: "18px",
                  fontWeight: "500",
                }}
              >
                {isLoading ? "Sending..." : "Send code"}
              </IonButton>

              <button
                className="text-red-600 px-6 py-2 rounded-lg font-semibold"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {step === "otp" && (
          <div className="signin-card">
            <h2
              className="text-lg font-semibold mb-4"
              style={{ textAlign: "center" }}
            >
              Enter the verification code sent.
            </h2>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                {successMessage}
              </div>
            )}

            <div
              className="otp-input-container"
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "20px",
                justifyContent: "center",
              }}
            >
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el: any) => (inputRefs.current[index] = el)}
                  type="number"
                  inputMode="numeric"
                  autoComplete={isIOS() ? "one-time-code" : "off"}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  // onPaste={handlePaste} // ✅ added here
                  style={{
                    width: "40px",
                    height: "48px",
                    textAlign: "center",
                    fontSize: "24px",
                    borderRadius: "8px",
                    border: "1px solid #dadcdf",
                    outline: "none",
                  }}
                  pattern="\d*"
                />
              ))}
            </div>

            <div
              className="resend-container"
              style={{
                marginBottom: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <span className="resend-text" style={{ color: "#6c7278" }}>
                Didn&apos;t receive a text?
              </span>
              <button
                onClick={handleResendOtp}
                className="resend-button"
                style={{
                  background: "none",
                  border: "none",
                  color: "#0778cf",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
                disabled={isLoading || resendTimer > 0}
              >
                Send again{resendTimer > 0 ? ` (${resendTimer}s)` : ""}
              </button>
            </div>

            <IonButton
              expand="block"
              onClick={() => handleVerify()}
              disabled={isLoading || otp.join("").length !== 6}
              className="primary-button w-full mb-4"
              style={{
                "--border-radius": "30px",
                fontSize: "18px",
                fontWeight: "500",
              }}
            >
              {isLoading ? "Verifying..." : "Verify"}
            </IonButton>
          </div>
        )}
      </div>
    </IonModal>
  );
};

export default OtpPopup;
